/**
 * Export dialog for embedding a diagram in an external page.
 *
 * Shown after the workspace has compiled and bundled the project: presents
 * the copyable iframe snippet and raw embed URL (snapshot in the URL
 * fragment), a downloadable snapshot JSON for the `?src=` transport, and the
 * encoded payload size with a warning when the URL risks truncation.
 *
 * The parent owns the dialog state (compile happens there) and passes the
 * current phase via `state`; the dialog renders loading/error/ready bodies
 * accordingly and reports dismissal via `onClose`.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import type { EmbedPayload } from "../embed/payload";
import {
  EMBED_PAYLOAD_WARN_CHARS,
  buildEmbedUrl,
  buildIframeSnippet,
  isPayloadOversized,
  snapshotFileName,
} from "../embed/snippet";

/** Phases of the embed export flow, owned by the workspace. */
export type EmbedDialogState =
  | { kind: "loading" }
  | { kind: "compile-error" }
  | { kind: "error"; message: string }
  | { kind: "ready"; payload: EmbedPayload; encoded: string };

interface EmbedDialogProps {
  state: EmbedDialogState;
  onClose: () => void;
}

/** Copy button with transient "Copied!" feedback. */
function CopyButton({
  text,
  title,
  testId,
}: {
  text: string;
  title: string;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // Clear the pending "Copied!" reset when the dialog unmounts so the
  // timeout does not fire setState on an unmounted component.
  useEffect(
    () => () => {
      clearTimeout(resetTimer.current);
    },
    [],
  );

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopied(true);
        // Re-clicking before the reset fires restarts the 2s window.
        clearTimeout(resetTimer.current);
        resetTimer.current = setTimeout(() => {
          setCopied(false);
        }, 2000);
      },
      () => {
        // Clipboard write can fail in insecure contexts; the user can still
        // select the adjacent text manually, so do not block the dialog.
        console.error("clipboard write failed");
      },
    );
  }, [text]);

  return (
    <button
      type="button"
      className="sim-btn embed-dialog-copy-btn"
      onClick={handleCopy}
      title={title}
      data-testid={testId}
    >
      {copied ? "Copied!" : "Copy"}
    </button>
  );
}

/** Trigger a browser download of the snapshot payload as plain JSON. */
function downloadSnapshot(payload: EmbedPayload): void {
  const blob = new Blob([JSON.stringify(payload)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = snapshotFileName(payload.title ?? "");
  anchor.click();
  URL.revokeObjectURL(url);
}

const EmbedDialog: React.FC<EmbedDialogProps> = ({ state, onClose }) => {
  // Close on Escape so keyboard users are not trapped in the dialog.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      className="embed-dialog-backdrop"
      onClick={onClose}
      data-testid="embed-dialog"
    >
      <div
        className="embed-dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Embed this diagram"
        onClick={(event) => {
          // Clicks inside the panel must not bubble to the backdrop-close.
          event.stopPropagation();
        }}
      >
        <div className="embed-dialog-header">
          <h2>Embed this diagram</h2>
          <button
            type="button"
            className="sim-btn embed-dialog-close"
            onClick={onClose}
            title="Close the embed dialog"
          >
            ✕
          </button>
        </div>
        <EmbedDialogBody state={state} />
      </div>
    </div>
  );
};

function EmbedDialogBody({ state }: { readonly state: EmbedDialogState }) {
  switch (state.kind) {
    case "loading":
      return <p className="embed-dialog-status">Building the snapshot…</p>;
    case "compile-error":
      return (
        <p className="embed-dialog-error" role="alert">
          The project has compile errors, so an embed would not run. Fix the
          errors in the editor and try again.
        </p>
      );
    case "error":
      return (
        <p className="embed-dialog-error" role="alert">
          Building the embed failed: {state.message}
        </p>
      );
    case "ready":
      return <EmbedDialogReady state={state} />;
  }
}

function EmbedDialogReady({
  state,
}: {
  readonly state: Extract<EmbedDialogState, { kind: "ready" }>;
}) {
  const { payload, encoded } = state;
  const origin = window.location.origin;
  const url = buildEmbedUrl(origin, encoded);
  const snippet = buildIframeSnippet({
    origin,
    encodedPayload: encoded,
    title: payload.title ?? "vibediagram simulation",
  });
  const oversized = isPayloadOversized(encoded);

  return (
    <div className="embed-dialog-body">
      <section className="embed-dialog-section">
        <div className="embed-dialog-section-header">
          <h3>Iframe snippet</h3>
          <CopyButton
            text={snippet}
            title="Copy the iframe snippet to the clipboard"
            testId="embed-copy-snippet"
          />
        </div>
        <p className="embed-dialog-hint">
          Paste this into your blog post's HTML. The snapshot travels in the URL
          — nothing is stored on a server.
        </p>
        <pre className="embed-dialog-code" data-testid="embed-snippet">
          {snippet}
        </pre>
      </section>

      <section className="embed-dialog-section">
        <div className="embed-dialog-section-header">
          <h3>Embed URL</h3>
          <CopyButton
            text={url}
            title="Copy the embed URL to the clipboard"
            testId="embed-copy-url"
          />
        </div>
        <pre className="embed-dialog-code" data-testid="embed-url">
          {url}
        </pre>
      </section>

      <section className="embed-dialog-section">
        <div className="embed-dialog-section-header">
          <h3>Snapshot file</h3>
          <button
            type="button"
            className="sim-btn"
            onClick={() => {
              downloadSnapshot(payload);
            }}
            title="Download the snapshot as a JSON file to host next to your post"
            data-testid="embed-download-snapshot"
          >
            Download snapshot .json
          </button>
        </div>
        <p className="embed-dialog-hint">
          Alternative for very long URLs: host the file next to your post and
          embed <code>{`${origin}/embed?src=<file URL>`}</code>. The host must
          allow cross-origin requests (CORS).
        </p>
      </section>

      <p className="embed-dialog-size" data-testid="embed-payload-size">
        Encoded payload size: {encoded.length.toLocaleString()} characters
        {oversized && (
          <span className="embed-dialog-size-warning" role="alert">
            {" — "}this exceeds {EMBED_PAYLOAD_WARN_CHARS.toLocaleString()}{" "}
            characters; some platforms truncate long URLs. Prefer the snapshot
            file above.
          </span>
        )}
      </p>
    </div>
  );
}

export default EmbedDialog;
